/**
 * Auth Context (Supabase-based)
 * Manages authentication state across the app using Supabase Auth
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, signInWithPassword, signOut, onAuthStateChange } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [currentClinic, setCurrentClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supabaseUser, setSupabaseUser] = useState(null);

  // Load user data from our backend when Supabase session exists
  const loadUserData = useCallback(async (supabaseSession) => {
    if (!supabaseSession) {
      setUser(null);
      setClinics([]);
      setCurrentClinic(null);
      setSupabaseUser(null);
      return;
    }

    try {
      // Set token for API client
      const token = supabaseSession.access_token;
      localStorage.setItem('supabase_token', token);

      // Load user profile from our backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load user profile');
      }

      const userData = await response.json();
      setUser(userData);
      setSupabaseUser(supabaseSession.user);

      // Extract clinics from memberships
      const userClinics = userData.memberships?.map(m => ({
        ...m.clinic,
        role: m.role,
        joinedAt: m.joinedAt,
      })) || [];

      setClinics(userClinics);

      // Set current clinic (from localStorage or first clinic)
      if (userClinics.length > 0) {
        const savedClinicId = localStorage.getItem('currentClinicId');
        const clinic = userClinics.find(c => c.id === savedClinicId) || userClinics[0];
        setCurrentClinic(clinic);
        // Save to localStorage for API client
        localStorage.setItem('currentClinicId', clinic.id);
      }
    } catch (err) {
      console.error('Failed to load user data:', err);
      setError(err.message);
      // Clear Supabase session if we can't load user from backend
      await signOut();
    }
  }, []);

  // Listen to Supabase auth changes
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      loadUserData(session).finally(() => setLoading(false));
    });

    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        await loadUserData(session);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setClinics([]);
        setCurrentClinic(null);
        setSupabaseUser(null);
        localStorage.removeItem('supabase_token');
        localStorage.removeItem('currentClinicId');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadUserData]);

  // Login with email/password
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { session } = await signInWithPassword(email, password);
      await loadUserData(session);
      return session;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [loadUserData]);

  // Logout
  const logout = useCallback(async () => {
    await signOut();
    // State will be cleared by onAuthStateChange listener
  }, []);

  // Update profile (call backend)
  const updateProfile = useCallback(async (updates) => {
    const token = localStorage.getItem('supabase_token');
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/me`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    const updatedUser = await response.json();
    setUser(prev => ({ ...prev, ...updatedUser }));
    return updatedUser;
  }, []);

  // Switch clinic - accepts either a clinic object or a clinic ID string
  const switchClinic = useCallback((clinicOrId) => {
    const clinic = typeof clinicOrId === 'string'
      ? clinics.find(c => c.id === clinicOrId)
      : clinicOrId;
    if (clinic) {
      setCurrentClinic(clinic);
      localStorage.setItem('currentClinicId', clinic.id);
    }
  }, [clinics]);

  // Refresh clinics
  const refreshClinics = useCallback(async () => {
    const token = localStorage.getItem('supabase_token');
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/clinics`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh clinics');
    }

    const clinicsData = await response.json();
    setClinics(clinicsData);
    // Also update currentClinic if it's in the refreshed list
    setCurrentClinic(prev => {
      if (prev) {
        const updated = clinicsData.find(c => c.id === prev.id);
        if (updated) return updated;
      }
      return prev;
    });
    return clinicsData;
  }, []);

  const value = {
    user,
    supabaseUser,
    clinics,
    currentClinic,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    updateProfile,
    switchClinic,
    refreshClinics,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
